// Shared utility functions

/**
 * Get the organization slug from the subdomain
 * @returns {string|null} The organization slug or null if on main domain
 */
// Subdomains reserved for internal services (not organizations)
const RESERVED_SUBDOMAINS = ['www', 'docs', 'api', 'admin', 'pickup']

/**
 * True when the current host is the pickup app (pickup.iceops.app or
 * pickup.localhost for local dev).
 * @returns {boolean}
 */
export const isPickupSubdomain = () => {
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  return parts[0] === 'pickup' && parts.length >= 2
}

export const getOrganizationSlugFromSubdomain = () => {
  const hostname = window.location.hostname

  // Skip if already on main domain or localhost without subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null
  }

  const parts = hostname.split('.')

  // If we have more than 2 parts and the first part is not a reserved subdomain
  // then it's likely an organization (e.g., "myorg" from "myorg.iceops.app")
  if (parts.length > 2 && !RESERVED_SUBDOMAINS.includes(parts[0])) {
    return parts[0]
  }

  // For localhost testing (e.g., myorg.localhost)
  if (parts.length === 2 && parts[1] === 'localhost') {
    return parts[0]
  }

  return null
}

/**
 * Build a subdomain URL for an organization
 * @param {string} slug - The organization slug
 * @returns {string} The complete subdomain URL
 */
export const buildSubdomainUrl = (slug) => {
  const hostname = window.location.hostname
  const protocol = window.location.protocol
  const port = window.location.port

  // For localhost, use format: slug.localhost:port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${slug}.localhost${port ? ':' + port : ''}`
  }

  // For production (e.g., iceops.app), use format: slug.iceops.app
  // Get base domain (e.g., iceops.app from www.iceops.app or myorg.iceops.app)
  const parts = hostname.split('.')
  const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname

  return `${protocol}//${slug}.${baseDomain}${port ? ':' + port : ''}`
}

/**
 * Get the current subdomain (for backwards compatibility)
 * @returns {string|null} The subdomain or null
 */
export const getSubdomain = () => {
  return getOrganizationSlugFromSubdomain()
}
